import { useCallback, useEffect, useState } from 'react';
import { CommonConstant, CommonUtility, InventoryService, RackService } from '../utility';
import {
  inventorySuggestedTransfersListResponseSchema,
  type InventorySuggestedTransferItem,
  type SuggestedTransferListFilter,
} from '../schemas/inventorySuggestedTransfers';
import {
  replenishmentWavesListResponseSchema,
  type ReplenishmentWave,
  type ReplenishmentWavesListFilter,
} from '../schemas/replenishmentWaves';
import { useDebouncedEffect } from './debounce';

export const InventoryFeedsHook = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 10,
    page: 1,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const temp = {
        ...filter,
      };
      const res = await InventoryService.getFeeds(temp);
      setData(res?.data?.feeds);
      setTotalData(res?.totalCount);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      fetchData();
    },
    [filter, refreshKey],
    500,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    refresh,
  };
};

export const InventoryListHook = (warehouseId, expired) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 10,
    page: 1,
    search: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      let temp: Record<string, unknown> = {
        ...filter,
        warehouseId,
      };

      if (expired) {
        temp.expiryFilter = 'expired';
      }

      if (typeof temp.search === 'string' && temp.search) {
        temp.search = temp.search.trim();
      }

      temp = CommonUtility.removeEmptyFields(temp);

      const res = await InventoryService.get(temp);
      setData(res?.data);
      setTotalData(res?.totalCount);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (warehouseId) {
        fetchData();
      }
    },
    [filter, refreshKey, warehouseId, expired],
    500,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  const updateAllFilter = obj => {
    setFilter(filter => ({
      ...filter,
      ...obj,
    }));
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    updateAllFilter,
    refresh,
  };
};

export const InventoryTransferListHook = warehouseId => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 10,
    page: 1,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const temp = {
        ...filter,
        warehouseId,
      };
      const res = await InventoryService.getInventoryTranfers(temp);
      setData(res?.transfers);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (warehouseId) {
        fetchData();
      }
    },
    [filter, refreshKey, warehouseId],
    500,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    refresh,
    setLoading,
  };
};

const SUGGESTED_TRANSFER_AUTO_REFRESH_MS = 60_000;

export const InventoryTransferSuggestedListHook = (warehouseId: string | undefined) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<InventorySuggestedTransferItem[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filter, setFilter] = useState<SuggestedTransferListFilter>({
    limit: 10,
    page: 1,
    sort: {
      sortBy: 'suggestedQuantity',
      sortOrder: CommonConstant.sortDirection.descending,
    },
  });

  const fetchData = useCallback(async () => {
    if (!warehouseId) {
      setLoading(false);
      setData([]);
      setTotalData(0);
      setTotalPages(0);
      return [];
    }

    setLoading(true);
    try {
      setError(null);
      const { sort, hasUnassignedAllocations, ...filterParams } = filter;
      const trimmedSearch = filter.search?.trim();
      const temp = {
        ...filterParams,
        ...(hasUnassignedAllocations ? { hasUnassignedAllocations } : {}),
        ...sort,
        search: trimmedSearch || undefined,
        warehouseId,
      };
      const res = await InventoryService.getInventorySuggestedTranfers(temp);
      const parsed = inventorySuggestedTransfersListResponseSchema.parse(res);

      setData(parsed.data);
      setTotalData(parsed.total);
      setTotalPages(parsed.totalPages);
      return parsed.data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [filter, warehouseId]);

  useDebouncedEffect(
    () => {
      fetchData().catch(() => undefined);
    },
    [fetchData],
    500,
  );

  useEffect(() => {
    if (!warehouseId) return undefined;
    const timer = window.setInterval(() => {
      fetchData().catch(() => undefined);
    }, SUGGESTED_TRANSFER_AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [fetchData, warehouseId]);

  const pageChanged = useCallback((page: number) => {
    setFilter(prev => ({
      ...prev,
      page,
    }));
  }, []);

  const filterChanged = useCallback(
    <K extends keyof SuggestedTransferListFilter>(
      key: K,
      value: SuggestedTransferListFilter[K],
    ) => {
      setFilter(prev => ({
        ...prev,
        page: 1,
        [key]: value,
      }));
    },
    [],
  );

  const setSearch = useCallback(
    (search: string) => {
      filterChanged('search', search);
    },
    [filterChanged],
  );

  const onSortChange = useCallback(
    (sortObject: Partial<Record<'suggestedQuantity', string>>) => {
      if (!CommonUtility.isValidObject(sortObject)) return;
      const sortKey = Object.keys(sortObject)[0] as 'suggestedQuantity' | undefined;
      const sortOrder = sortKey ? sortObject[sortKey] : undefined;
      if (!sortKey || !sortOrder) return;

      setFilter(prev => ({
        ...prev,
        page: 1,
        sort: {
          sortBy: sortKey,
          sortOrder,
        },
      }));
    },
    [],
  );

  const refresh = useCallback(() => {
    fetchData().catch(() => undefined);
  }, [fetchData]);
  const refreshAsync = useCallback(() => fetchData(), [fetchData]);

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    onSortChange,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    refresh,
    refreshAsync,
    setLoading,
  };
};

export const InventoryReplenishmentWavesListHook = (warehouseId: string | undefined) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ReplenishmentWave[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState<ReplenishmentWavesListFilter>({
    limit: 20,
    page: 1,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      setError(null);
      const temp = {
        ...filter,
        warehouseId,
      };
      const res = await InventoryService.getReplenishmentWaves(temp);
      const parsed = replenishmentWavesListResponseSchema.parse(res);
      setData(parsed.data);
      setTotalData(parsed.total);
      setTotalPages(parsed.totalPages);
    } catch (e) {
      setError(e);
      setData([]);
      setTotalData(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (!warehouseId) {
        setLoading(false);
        setData([]);
        setTotalData(0);
        setTotalPages(0);
        return;
      }
      fetchData();
    },
    [filter, refreshKey, warehouseId],
    500,
  );

  const pageChanged = (page: number) => {
    setFilter(prev => ({ ...prev, page }));
  };

  const filterChanged = <K extends keyof ReplenishmentWavesListFilter>(
    key: K,
    value: ReplenishmentWavesListFilter[K],
  ) => {
    setFilter(prev => ({
      ...prev,
      page: 1,
      [key]: value,
    }));
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    totalData,
    totalPages,
    refresh,
  };
};

export const InventoryLogsListHook = inventoryId => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [item, setItem] = useState({});
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 25,
    page: 1,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const temp = {
        ...filter,
        inventoryId,
      };
      const res = await InventoryService.getInventoryHistory(inventoryId, temp);
      setData(res?.data);
      setTotalData(res?.totalCount);
      setTotalPages(res?.totalPages);
      setItem({
        product: res?.product || {},
        currentState: res?.currentState || {},
        finalCalculatedState: res?.finalCalculatedState || {},
        initialState: res?.initialState || {},
        warehouseId: res?.warehouse?._id || {},
        location: res?.location || {},
        hasDiscrepancies: res?.hasDiscrepancies,
      });
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (inventoryId) {
        fetchData();
      }
    },
    [filter, refreshKey, inventoryId],
    500,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  return {
    data,
    item,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    refresh,
    setLoading,
  };
};

export const useLotAttributesHook = id => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await InventoryService.getLotAttributes(id);
      setData(data || []);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setData({});
    }
  }, [id]);

  return { data, loading, error, refreshData: fetchData };
};

export const skuAutocompleteHook = (organization_id, search) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const temp = {
        search,
        organization_id,
      };
      if (temp.search) {
        temp.search = temp.search.trim();
      }
      const res = await InventoryService.getRelatedSku(temp);
      const mapped = res?.data.map(d => ({ ...d, search }));
      setData(mapped);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!organization_id || !search) {
      return;
    }

    setData([]);
    fetchData();
  }, [search, organization_id]);

  return { data, error, loading };
};

export const inventoryPropsalHook = (
  warehouseId,
  productId,
  currentQuantity,
  newInventory,
  search,
) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('fetching');
      const payload: Record<string, unknown> = {
        warehouseId,
        items: [
          {
            productId,
            inventoryType: 'case',
            quantity: {
              total: currentQuantity,
              available: newInventory,
            },
          },
        ],
      };

      if (search) {
        payload.search = search.trim();
      }
      const res = await InventoryService.getInventoryProposals(payload);
      console.log('res?.[0]?.proposedLocations: ', res?.[0]?.proposedLocations);
      setData(res?.[0]?.proposedLocations);
    } catch (error) {
      console.log('er: ', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      console.log('s: ', search);
      console.log('warehouseId: ', warehouseId);
      console.log('productId: ', productId);
      console.log('currentQuantity: ', CommonUtility.isNotNull(currentQuantity));
      if (!warehouseId || !productId || !CommonUtility.isNotNull(currentQuantity)) {
        console.log('here :P');
        setData([]);
        return;
      }

      fetchData();
    },
    [search, warehouseId, productId, currentQuantity, newInventory],
    500,
  );

  return { data, error, loading };
};

export const ReplenishmentHook = warehouseId => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 10,
    page: 1,
    search: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const temp = {
        ...filter,
        warehouseId,
      };

      if (temp.search) {
        temp.search = temp.search.trim();
      } else {
        delete temp.search;
      }

      const res = await InventoryService.replenishmentInventory(temp);
      setData(res?.data);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (warehouseId) {
        fetchData();
      }
    },
    [filter, refreshKey, warehouseId],
    500,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    refresh,
  };
};
export const ConsolidatedReplenishmentHook = (warehouseId, isIgnored) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 10,
    page: 1,
    search: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const temp: Record<string, unknown> = {
        ...filter,
        warehouseId,
      };

      if (!temp.isIgnored) {
        delete temp.isIgnored;
      }

      if (typeof temp.search === 'string' && temp.search) {
        temp.search = temp.search.trim();
      } else {
        delete temp.search;
      }

      const res = await InventoryService.replenishmentInventoryConsolidated(temp);
      console.log('res: ', res);
      setData(res?.data);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      console.log('error: ', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (warehouseId) {
        fetchData();
      }
    },
    [filter, refreshKey, warehouseId],
    500,
  );

  useEffect(() => {
    filterChanged('isIgnored', isIgnored);
  }, [isIgnored]);

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    refresh,
  };
};

export const LocationSearchHook = (warehouseId, search) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params: Record<string, unknown> = { limit: 20 };

      if (search) {
        params.search = search.trim();
      }
      const res = await InventoryService.getInventoryLocation(warehouseId, params);
      setData(res?.data?.map(loc => ({ ...loc, search })));
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (!warehouseId) {
        setData([]);
        return;
      }

      fetchData();
    },
    [search, warehouseId],
    500,
  );

  return { data, error, loading };
};

export const RackSearchHook = (warehouseId, search) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params: Record<string, unknown> = { limit: 20, warehouseId };

      if (search) {
        params.search = search.trim();
      }
      const res = await RackService.get(params);
      setData(res?.data?.map(loc => ({ ...loc, search })));
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (!warehouseId) {
        setData([]);
        return;
      }

      fetchData();
    },
    [search, warehouseId],
    500,
  );

  return { data, error, loading };
};
